import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { merge } from "lodash";
import { App, fullEndpoints, fullRoutes } from "@songtradr-sso/shared/common";
import { NormalizedException, redirect } from "../..";

/** Configuration for our Axios client */
type Config = {
  /** Authentication handlers */
  auth?: {
    /** Request interceptor (ex: token injection) */
    request?: (req: InternalAxiosRequestConfig) => InternalAxiosRequestConfig;
    /** Response interceptor (ex: token cleanup) */
    response?: (res: AxiosResponse) => AxiosResponse;
    /** Token refresh handlers */
    refresh?: {
      /** Request handler (ex: token refresh) */
      request?: (client: AxiosInstance) => Promise<void>;
      /** Redirect handler (ex: redirect to sign-in page) */
      redirect?: () => Promise<void>;
    };
  };
};

/** Default Axios client configuration */
const defaultConfig = {
  auth: {
    request: (req) => req,
    response: (res) => res,
    refresh: {
      request: (c) =>
        c.request({
          method: "POST",
          url: fullEndpoints[App.Core].auth.refreshToken,
        }),
      redirect: () =>
        redirect(fullRoutes[App.Core].base + window.location.search),
    },
  },
} satisfies Config;

/** Create Axios client
 *
 * Creates an axios instance and attaches auth handlers from a provided config.
 *
 * @param config Custom configuration for our axios client
 *
 * @returns Configured Axios client
 */
export const createAxiosClient = (config: Config = {}) => {
  // Hydrate config with defaults and destructure...
  const { auth } = merge({}, defaultConfig, config);
  const { refresh } = auth;

  // Create base axios client...
  const _client = axios.create({
    timeout: 60000,
    withCredentials: true,
  });

  // Attach auth handlers as request & response interceptors...
  _client.interceptors.request.use(auth.request);
  _client.interceptors.response.use(auth.response);

  // Create refresh handler as response interceptor...
  _client.interceptors.response.use(
    (res) => res,
    async (err) => {
      const { status, code } = new NormalizedException(err, false);
      if (status === 401) {
        if (!err.config._retry && code === "JwtVerify.ExpiredToken") {
          err.config._retry = true;
          await refresh.request(_client).catch(async () => {
            await refresh.redirect();
          });
          return _client(err.config);
        } else {
          await refresh.redirect();
        }
      }
      return Promise.reject(err);
    },
  );

  return _client;
};
